/** biome-ignore-all lint/suspicious/noExplicitAny: <explanation> */
import type { Port, PostalInfo } from '@/views/screens/common/types';

import { DEFAULT_STORE_OBJECT } from '../constants';
import type { RootState } from '../RootReducer';
import type { DefaultStoreObject } from '../types';
import CommonTypes from './CommonTypes';

export interface CommonState extends Record<string, any> {
  postalSearch: DefaultStoreObject<PostalInfo[]> & {
    requestData: {
      PageIndex: number;
      PageSize: number;
      CountryCode?: string;
      StateCode?: string;
      City?: string;
      PostalCode?: string;
    };
    totalRecords: number;
  };
  appLoader: boolean;
  fullScreenStatus: boolean;
  filterVisible: boolean;
  shipmentSidebarVisible: boolean;
  pageBlocker: boolean;
  quoteModalBlocker: boolean;
  portSearch: DefaultStoreObject<Port[]> & {
    totalRecords?: number;
    requestData?: { PageNumber: number; PageSize: number };
  };
}

const initialState: CommonState = {
  activeAccordion: [],
  appLoader: true,
  bidExpirationDate: null,
  carrierDetails: {
    ...DEFAULT_STORE_OBJECT,
    data: null,
  },
  carrierSearch: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    requestData: { PageNumber: 1, PageSize: 10 },
    totalRecords: 0,
  },
  copyQuoteKey: null,
  createSpotQuote: {},
  currentSpotCarriers: [],
  currentSpotCollaborators: [],
  currentSpotQuoteNoAndTitle: { data: { quoteKey: '', quoteNo: '', quoteTitle: '' } },
  currentSpotRoutingGuidesParams: {},
  documentTypesList: { data: [], success: false },
  filterVisible: true,
  fullScreenStatus: false,
  LADProcessor: { data: [], success: false },
  LADRegion: { data: [], success: false },
  LDCommodityCode: { data: [], success: false },
  LDLidLabel: { data: [], success: false },
  lossAndDamageStatus: { data: [], success: false },
  openForBidError: {
    bidDuration: false,
  },
  pageBlocker: false,
  palletPackageTypeList: { data: [], success: false },
  portSearch: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    requestData: { PageNumber: 1, PageSize: 10 },
    totalRecords: 0,
  },
  postalSearch: {
    ...DEFAULT_STORE_OBJECT,
    data: [],
    requestData: { PageIndex: 1, PageSize: 10 },
    totalRecords: 0,
  },
  quoteModalBlocker: false,
  shipmentAppointmentTypes: { data: [], success: false },
  shipmentProductDimUomList: { data: [], success: false },
  shipmentProductQtyUomList: { data: [], success: false },
  shipmentSidebarVisible: false,
  shipmentStopTypes: { data: [], success: false },
  spotQuoteCreator: { data: '', success: false },
  spotQuoteTransportationModes: { data: [], success: false },
  timeZonesList: { data: [], success: false },
  warningModal: { description: '', open: false },
};

const CommonReducer = (state = initialState, { type, payload }: { type: string; payload: any }): CommonState => {
  switch (type) {
    case CommonTypes.SET_FILTER_TOGGLE: {
      return { ...state, filterVisible: payload };
    }

    case CommonTypes.SET_SHIPMENT_SIDE_BAR_TOGGLE: {
      return { ...state, shipmentSidebarVisible: payload };
    }

    case CommonTypes.SET_APP_LOADER: {
      return { ...state, appLoader: payload };
    }

    case CommonTypes.SET_FULL_SCREEN_INSTANCE: {
      return { ...state, fullScreen: payload };
    }

    case CommonTypes.SET_PAGE_BLOCKER: {
      return { ...state, pageBlocker: payload };
    }

    case CommonTypes.SET_DOCUMENT_TYPES_LIST: {
      return { ...state, documentTypesList: payload };
    }

    case CommonTypes.SET_WARNING_MODAL: {
      return { ...state, warningModal: { ...state.warningModal, ...payload } };
    }

    case CommonTypes.GET_POSTAL_SEARCH_PROGRESS: {
      return {
        ...state,
        postalSearch: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.PageIndex === 1 ? [] : state.postalSearch.data,
          progressing: true,
          requestData: payload,
          totalRecords: state.postalSearch.totalRecords,
        },
      };
    }
    case CommonTypes.GET_POSTAL_SEARCH_SUCCESS: {
      return {
        ...state,
        postalSearch: {
          ...state.postalSearch,
          ...DEFAULT_STORE_OBJECT,
          data: [...(state.postalSearch.data || []), ...payload.data],
          success: true,
          totalRecords: payload.totalRecords,
        },
      };
    }
    case CommonTypes.GET_POSTAL_SEARCH_ERROR: {
      return {
        ...state,
        postalSearch: {
          ...state.postalSearch,
          ...DEFAULT_STORE_OBJECT,
          data: [],
          error: true,
          progressing: false,
        },
      };
    }
    case CommonTypes.GET_POSTAL_SEARCH_RESET: {
      return {
        ...state,
        postalSearch: {
          ...DEFAULT_STORE_OBJECT,
          data: [],
          requestData: state.postalSearch?.requestData,
          totalRecords: 0,
        },
      };
    }

    case CommonTypes.GET_PORT_SEARCH_PROGRESS: {
      return {
        ...state,
        portSearch: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.PageNumber === 1 ? [] : state.portSearch.data,
          progressing: true,
          requestData: payload,
        },
      };
    }
    case CommonTypes.GET_PORT_SEARCH_SUCCESS: {
      return {
        ...state,
        portSearch: {
          ...state.portSearch,
          ...DEFAULT_STORE_OBJECT,
          data: [...(state.portSearch.data || []), ...payload.data],
          success: true,
          totalRecords: payload.totalRecords,
        },
      };
    }
    case CommonTypes.GET_PORT_SEARCH_ERROR: {
      return {
        ...state,
        portSearch: {
          ...state.portSearch,
          ...DEFAULT_STORE_OBJECT,
          data: [],
          error: true,
        },
      };
    }
    case CommonTypes.GET_PORT_SEARCH_RESET: {
      return {
        ...state,
        portSearch: {
          ...(state.portSearch?.requestData || {}),
          ...DEFAULT_STORE_OBJECT,
          data: [] as Port[],
          totalRecords: 0,
        },
      };
    }

    case CommonTypes.GET_CARRIER_SEARCH_PROGRESS: {
      return {
        ...state,
        carrierSearch: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.PageIndex === 1 ? [] : state.carrierSearch.data,
          progressing: true,
          requestData: payload,
        },
      };
    }
    case CommonTypes.GET_CARRIER_SEARCH_SUCCESS: {
      return {
        ...state,
        carrierSearch: {
          ...state.carrierSearch,
          ...DEFAULT_STORE_OBJECT,
          data: [...state.carrierSearch.data, ...payload.data],
          success: true,
          totalRecords: payload.totalRecords,
        },
      };
    }
    case CommonTypes.GET_CARRIER_SEARCH_ERROR: {
      return {
        ...state,
        carrierSearch: {
          ...state.carrierSearch,
          ...DEFAULT_STORE_OBJECT,
          data: [],
          error: true,
          message: payload?.message,
        },
      };
    }
    case CommonTypes.GET_CARRIER_SEARCH_RESET: {
      return {
        ...state,
        carrierSearch: {
          ...state.carrierSearch?.requestData,
          ...DEFAULT_STORE_OBJECT,
          data: [],
          totalRecords: 0,
        },
      };
    }

    case CommonTypes.SET_LOSS_AND_DAMAGE_STATUS_LIST: {
      return {
        ...state,
        lossAndDamageStatus: payload,
      };
    }

    case CommonTypes.SET_LAD_CLAIMS_REGION_LIST: {
      return {
        ...state,
        LADRegion: payload,
      };
    }

    case CommonTypes.SET_LAD_CLAIM_PROCESSOR_LIST: {
      return {
        ...state,
        LADProcessor: payload,
      };
    }

    case CommonTypes.SET_LD_CLAIMS_COMMODITY_CODE_LIST: {
      return {
        ...state,
        LDCommodityCode: payload,
      };
    }

    case CommonTypes.SET_LD_CLAIMS_LID_LABEL_LIST: {
      return {
        ...state,
        LDLidLabel: payload,
      };
    }

    case CommonTypes.GET_CARRIER_DETAILS_PROGRESS: {
      return {
        ...state,
        carrierDetails: {
          ...DEFAULT_STORE_OBJECT,
          data: null,
          progressing: true,
        },
      };
    }
    case CommonTypes.GET_CARRIER_DETAILS_SUCCESS: {
      return {
        ...state,
        carrierDetails: {
          ...state.carrierDetails,
          progressing: false,
          success: true,
          ...payload,
        },
      };
    }
    case CommonTypes.GET_CARRIER_DETAILS_ERROR: {
      return {
        ...state,
        carrierDetails: {
          ...state.carrierDetails,
          data: null,
          error: true,
          progressing: false,
          ...payload,
        },
      };
    }

    case CommonTypes.SET_SHIPMENT_STOP_TYPES_LIST: {
      return {
        ...state,
        shipmentStopTypes: payload,
      };
    }

    case CommonTypes.SET_SHIPMENT_APPOINTMENT_TYPES_LIST: {
      return {
        ...state,
        shipmentAppointmentTypes: payload,
      };
    }

    case CommonTypes.SET_SHIPMENT_PRODUCT_QTY_UOM_LIST: {
      return {
        ...state,
        shipmentProductQtyUomList: payload,
      };
    }

    case CommonTypes.SET_SHIPMENT_PRODUCT_DIM_UOM_LIST: {
      return {
        ...state,
        shipmentProductDimUomList: payload,
      };
    }

    case CommonTypes.SET_PALLET_PACKAGE_TYPE_LIST: {
      return {
        ...state,
        palletPackageTypeList: payload,
      };
    }

    case CommonTypes.GET_NEW_SPOT_QUOTE_NO_IN_PROGRESS: {
      return {
        ...state,
        currentSpotQuoteNoAndTitle: {
          ...DEFAULT_STORE_OBJECT,
          data: { quoteKey: 0, quoteNo: '', quoteTitle: '' },
          progressing: true,
        },
      };
    }

    case CommonTypes.GET_NEW_SPOT_QUOTE_NO_SUCCESS: {
      return {
        ...state,
        currentSpotQuoteNoAndTitle: {
          ...DEFAULT_STORE_OBJECT,
          data: payload,
          success: true,
        },
      };
    }

    case CommonTypes.GET_NEW_SPOT_QUOTE_NO_ERROR: {
      return {
        ...state,
        currentSpotQuoteNoAndTitle: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
        },
      };
    }

    case CommonTypes.GET_SPOT_QUOTE_CREATOR_SUCCESS: {
      return {
        ...state,
        spotQuoteCreator: {
          ...DEFAULT_STORE_OBJECT,
          data: payload,
        },
      };
    }

    case CommonTypes.GET_SPOT_QUOTE_CREATOR_ERROR: {
      return {
        ...state,
        spotQuoteCreator: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
        },
      };
    }

    case CommonTypes.CREATE_SPOT_QUOTE_PROGRESS: {
      return {
        ...state,
        createSpotQuote: {
          ...DEFAULT_STORE_OBJECT,
          data: {},
          progressing: true,
        },
      };
    }

    case CommonTypes.CREATE_SPOT_QUOTE_SUCCESS: {
      return {
        ...state,
        createSpotQuote: {
          ...DEFAULT_STORE_OBJECT,
          data: payload.data,
          success: true,
        },
      };
    }

    case CommonTypes.CREATE_SPOT_QUOTE_RESET: {
      return {
        ...state,
        createSpotQuote: {
          ...DEFAULT_STORE_OBJECT,
        },
      };
    }

    case CommonTypes.CREATE_SPOT_QUOTE_ERROR: {
      return {
        ...state,
        createSpotQuote: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
          ...payload,
          data: {},
        },
      };
    }
    case CommonTypes.SET_CURRENT_QUOTE_ROUTING_GUIDES_PARAMS: {
      return { ...state, currentSpotRoutingGuidesParams: payload };
    }

    case CommonTypes.RESET_CURRENT_QUOTE_ROUTING_GUIDES_PARAMS: {
      return { ...state, currentSpotRoutingGuidesParams: {} };
    }

    case CommonTypes.SET_CURRENT_QUOTE_COLLABORATORS: {
      return {
        ...state,
        currentSpotCollaborators: [...payload],
      };
    }

    case CommonTypes.ADD_TO_CURRENT_QUOTE_COLLABORATORS: {
      return {
        ...state,
        currentSpotCollaborators: [...state.currentSpotCollaborators, ...payload],
      };
    }

    case CommonTypes.REMOVE_CURRENT_QUOTE_COLLABORATORS: {
      // @ts-expect-error
      const newList = state.currentSpotCollaborators.filter((value) => !payload.includes(value.userId));

      return {
        ...state,
        currentSpotCollaborators: newList,
      };
    }

    case CommonTypes.RESET_CURRENT_QUOTE_COLLABORATORS: {
      return {
        ...state,
        currentSpotCollaborators: [],
      };
    }

    case CommonTypes.SET_CURRENT_QUOTE_CARRIERS: {
      return {
        ...state,
        currentSpotCarriers: [...payload],
      };
    }

    case CommonTypes.ADD_TO_CURRENT_QUOTE_CARRIERS: {
      return {
        ...state,
        currentSpotCarriers: [...state.currentSpotCarriers, ...payload],
      };
    }

    case CommonTypes.REMOVE_CURRENT_QUOTE_CARRIER: {
      const newList = state.currentSpotCarriers.filter(
        // @ts-expect-error
        (value) => value.carrierKey !== payload && value.carrierId !== payload,
      );

      return {
        ...state,
        currentSpotCarriers: newList,
      };
    }

    case CommonTypes.RESET_CURRENT_QUOTE_CARRIER: {
      return {
        ...state,
        currentSpotCarriers: [],
      };
    }

    case CommonTypes.SET_QUOTE_MODAL_BLOCKER: {
      return { ...state, quoteModalBlocker: payload };
    }

    case CommonTypes.SET_QUOTE_SPOT_BID_SAVE_ERROR: {
      return {
        ...state,
        openForBidError: { ...state.openForBidError, ...payload },
      };
    }

    case CommonTypes.SET_QUOTE_BID_EXPIRATION_DATE: {
      return { ...state, bidExpirationDate: payload };
    }

    case CommonTypes.RESET_QUOTE_BID_EXPIRATION_DATE: {
      return { ...state, bidExpirationDate: null };
    }

    case CommonTypes.SET_QUOTE_ACTIVE_ACCORDIONS: {
      return {
        ...state,
        activeAccordion: payload,
      };
    }

    case CommonTypes.GET_SPOT_QUOTE_TRANSPORTATION_MODES_IN_PROGRESS: {
      return {
        ...state,
        spotQuoteTransportationModes: {
          ...DEFAULT_STORE_OBJECT,
          progressing: true,
        },
      };
    }

    case CommonTypes.GET_SPOT_QUOTE_TRANSPORTATION_MODES_IN_SUCCESS: {
      return {
        ...state,
        spotQuoteTransportationModes: {
          ...DEFAULT_STORE_OBJECT,
          data: payload,
        },
      };
    }

    case CommonTypes.GET_SPOT_QUOTE_TRANSPORTATION_MODES_IN_ERROR: {
      return {
        ...state,
        spotQuoteTransportationModes: {
          ...DEFAULT_STORE_OBJECT,
          error: true,
        },
      };
    }

    case CommonTypes.SET_COPY_QUOTE_KEY: {
      return {
        ...state,
        copyQuoteKey: payload,
      };
    }

    case CommonTypes.RESET_COPY_QUOTE_KEY: {
      return {
        ...state,
        copyQuoteKey: null,
      };
    }

    default:
      return state;
  }
};

// biome-ignore lint/style/noDefaultExport: TODO: fix
export default CommonReducer;

export const selectIsPageBlockerActive = (state: RootState) => state.common.pageBlocker;
export const selectCurrentQuoteRoutingGuidesParams = (state: RootState) => state.common.currentSpotRoutingGuidesParams;
export const selectCurrentQuoteCollaborators = (state: RootState) => state.common.currentSpotCollaborators;
export const selectCurrentQuoteCarries = (state: RootState) => state.common.currentSpotCarriers;
export const selectQuoteKeyInProgress = (state: RootState) => {
  const keyObject = state.common.currentSpotQuoteNoAndTitle.data;

  if (keyObject?.quoteNo.length) {
    return keyObject.quoteNo.replace(/[^0-9]/g, '');
  }
  return '';
};
